import React from "react"
import ScrollIntoView from "react-scroll-into-view"

import {
  Wrapper,
  Media,
  Scrim,
  ContentZone,
  HeadlineRow,
  TitleCol,
  ParagraphCol,
  Eyebrow,
  Paragraph,
  Divider,
  DividerLine,
  LoopLabel,
  StepsRowWrap,
  LoopBracket,
  LoopArrowDown,
  StepsRow,
  StepCard,
  StepIconWrap,
  StepLabel,
  StepText,
  StepArrow,
  ResultsRow,
  ResultItem,
  ResultIconWrap,
  ResultLabel,
  ResultText,
  NextButtonRow,
  NextButton,
} from "./styles"
import SectionTitle from "../../UI/SectionTitle"

const iconProps = {
  viewBox: "0 0 24 24",
  fill: "none",
  strokeWidth: 1.6,
  strokeLinecap: "round",
  strokeLinejoin: "round",
}

const HourglassIcon = () => (
  <svg {...iconProps}>
    <path d="M6 3h12M6 21h12M7 3c0 4 3.5 6.5 5 8 1.5-1.5 5-4 5-8M7 21c0-4 3.5-6.5 5-8 1.5 1.5 5 4 5 8" />
  </svg>
)

const FlaskIcon = () => (
  <svg {...iconProps}>
    <path d="M9 3h6M10 3v6l-5 9a2 2 0 0 0 2 3h10a2 2 0 0 0 2-3l-5-9V3" />
    <path d="M7.5 15h9" />
  </svg>
)

const DatabaseIcon = () => (
  <svg {...iconProps}>
    <ellipse cx="12" cy="5" rx="8" ry="3" />
    <path d="M4 5v14c0 1.7 3.6 3 8 3s8-1.3 8-3V5" />
    <path d="M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3" />
  </svg>
)

const ModelsIcon = () => (
  <svg {...iconProps}>
    <rect x="7" y="7" width="10" height="10" rx="1.5" />
    <path d="M9 3v2M12 3v2M15 3v2M9 19v2M12 19v2M15 19v2M3 9h2M3 12h2M3 15h2M19 9h2M19 12h2M19 15h2" />
  </svg>
)

const RefreshIcon = () => (
  <svg {...iconProps}>
    <path d="M4 4v5h5" />
    <path d="M20 20v-5h-5" />
    <path d="M5.5 9A7 7 0 0 1 19 8M18.5 15A7 7 0 0 1 5 16" />
  </svg>
)

const PredictionIcon = () => (
  <svg {...iconProps}>
    <path d="M4 16l5-6 4 4 7-9" />
    <path d="M15 5h5v5" />
  </svg>
)

const GenerativeIcon = () => (
  <svg {...iconProps}>
    <path d="M12 3v4M12 17v4M3 12h4M17 12h4M6.3 6.3l2.1 2.1M15.6 15.6l2.1 2.1M17.7 6.3l-2.1 2.1M8.4 15.6l-2.1 2.1" />
  </svg>
)

const SpeedIcon = () => (
  <svg {...iconProps}>
    <circle cx="12" cy="13" r="8" />
    <path d="M12 13l3-3M9 3h6" />
  </svg>
)

const STEPS = [
  {
    label: "Functional experiments",
    text: "Activity, selectivity, properties, conditions & promiscuity.",
    Icon: FlaskIcon,
  },
  {
    label: "Functional data",
    text: "Structured ground truth, rich with metadata.",
    Icon: DatabaseIcon,
  },
  {
    label: "Models",
    text: "Predictive and generative, steered to purpose.",
    Icon: ModelsIcon,
  },
  {
    label: "Designed enzymes",
    text: "New chemistry and performance — tested in the wetlab.",
    Icon: HourglassIcon,
  },
]

const RESULTS = [
  {
    label: "Better predictions",
    text: "For novel enzyme sequences, function & properties.",
    Icon: PredictionIcon,
  },
  {
    label: "Smarter generative models",
    text: "Designing enzymes far beyond what exists in nature.",
    Icon: GenerativeIcon,
  },
  {
    label: "Faster discovery cycles",
    text: "Reducing the time and capital of new discovery.",
    Icon: SpeedIcon,
  },
]

const DataSection = ({ eyebrow, title, paragraph, videoUrl, posterUrl }) => {
  return (
    <Wrapper id="proprietary-data">
      {videoUrl ? (
        <Media as="video" src={videoUrl} poster={posterUrl || undefined} autoPlay muted loop playsInline />
      ) : posterUrl ? (
        <Media as="img" src={posterUrl} alt="" />
      ) : null}

      <Scrim />

      <ContentZone>
        <HeadlineRow>
          <TitleCol>
            {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
            {title ? (
              <SectionTitle light align="left">
                {title}
              </SectionTitle>
            ) : null}
          </TitleCol>
          <ParagraphCol>{paragraph ? <Paragraph>{paragraph}</Paragraph> : null}</ParagraphCol>
        </HeadlineRow>

        <Divider>
          <DividerLine />
        </Divider>

        <LoopLabel>
          <RefreshIcon />
          Active learning loop
        </LoopLabel>

        <StepsRowWrap>
          <LoopBracket />
          <LoopArrowDown viewBox="0 0 13 9">
            <polygon points="0,0 13,0 6.5,9" fill="currentColor" />
          </LoopArrowDown>
          <StepsRow>
            {STEPS.map(({ label, text, Icon }, i) => (
              <React.Fragment key={label}>
                <StepCard>
                  <StepIconWrap>
                    <Icon />
                  </StepIconWrap>
                  <StepLabel>{label}</StepLabel>
                  <StepText>{text}</StepText>
                </StepCard>
                {i < STEPS.length - 1 ? <StepArrow delay={(i + 1) * 1.2}>→</StepArrow> : null}
              </React.Fragment>
            ))}
          </StepsRow>
        </StepsRowWrap>

        <Divider>
          <DividerLine />
        </Divider>

        <ResultsRow>
          {RESULTS.map(({ label, text, Icon }) => (
            <ResultItem key={label}>
              <ResultIconWrap>
                <Icon />
              </ResultIconWrap>
              <div>
                <ResultLabel>{label}</ResultLabel>
                <ResultText>{text}</ResultText>
              </div>
            </ResultItem>
          ))}
        </ResultsRow>

        <NextButtonRow>
          <ScrollIntoView selector="#timeline">
            <NextButton>
              Our journey
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 5v14M5 12l7 7 7-7" />
              </svg>
            </NextButton>
          </ScrollIntoView>
        </NextButtonRow>
      </ContentZone>
    </Wrapper>
  )
}

export default DataSection
